import { useLang } from "../i18n/LanguageContext";
import { Phone, Mail, Instagram, Facebook, ShieldCheck, Award, BadgeCheck, MessageCircle, MapPin } from "lucide-react";
import { buildWhatsAppUrl } from "../lib/contact";

const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=10770+Columbia+Pike+Ste+300+%231313+Silver+Spring+MD+20901";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aaron-hvac/artifacts/fposwu68_6822fdb5-7f79-435c-9861-ad6a47053a6e.jpeg";

const COVERAGE = {
  "DC": ["NW", "NE", "SW", "SE"],
  "MD": ["Silver Spring", "Rockville", "Gaithersburg", "Bowie"],
  "VA": ["Arlington", "Alexandria", "Fairfax", "Woodbridge"],
};

const CERT_ICONS = [ShieldCheck, Award, BadgeCheck];

export const Footer = () => {
  const { t } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="relative bg-ink-deep border-t border-white/10">
      <div className="absolute inset-x-0 top-0 divider-temp" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
        {/* Brand */}
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Aarón Heating & Cooling" className="h-14 w-14 rounded-full object-cover ring-1 ring-white/10" />
            <div>
              <div className="font-heading font-extrabold text-white text-xl">Aarón</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/55">Heating &amp; Cooling</div>
            </div>
          </div>
          <p className="mt-5 text-sm text-white/65 leading-relaxed max-w-sm">{t.footer.tagline}</p>

          <div className="mt-6 flex items-center gap-3">
            <a href="https://instagram.com/" target="_blank" rel="noreferrer" aria-label="Instagram" className="h-10 w-10 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 inline-flex items-center justify-center" data-testid="social-instagram">
              <Instagram size={18} />
            </a>
            <a href="https://facebook.com/" target="_blank" rel="noreferrer" aria-label="Facebook" className="h-10 w-10 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 inline-flex items-center justify-center" data-testid="social-facebook">
              <Facebook size={18} />
            </a>
          </div>
        </div>

        {/* Coverage */}
        <div className="lg:col-span-5">
          <h4 className="font-heading font-bold text-white text-sm uppercase tracking-[0.2em]">{t.footer.coverage}</h4>
          <div className="mt-5 grid grid-cols-3 gap-4">
            {Object.entries(COVERAGE).map(([region, list], i) => (
              <div key={region}>
                <div className={`text-2xl font-heading font-extrabold ${i === 1 ? "text-heat" : "text-cool"}`}>{region}</div>
                <ul className="mt-2 space-y-1.5 text-sm text-white/65">
                  {list.map((z) => (
                    <li key={z}>{z}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Contact + Certs */}
        <div className="lg:col-span-3">
          <h4 className="font-heading font-bold text-white text-sm uppercase tracking-[0.2em]">{t.footer.contact}</h4>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <a href="tel:+12407618778" className="inline-flex items-center gap-2 text-white/85 hover:text-white">
                <Phone size={14} className="text-heat" /> 240-761-8778
              </a>
            </li>
            <li>
              <a href={buildWhatsAppUrl(t.whatsappMessage)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-white/85 hover:text-white">
                <MessageCircle size={14} className="text-[#25D366]" /> WhatsApp
              </a>
            </li>
            <li>
              <a href="mailto:aaronhvacllc@gmail.com" className="inline-flex items-center gap-2 text-white/85 hover:text-white break-all">
                <Mail size={14} className="text-cool" /> aaronhvacllc@gmail.com
              </a>
            </li>
            <li>
              <a href={MAPS_URL} target="_blank" rel="noreferrer" className="inline-flex items-start gap-2 text-white/85 hover:text-white">
                <MapPin size={14} className="text-cool mt-0.5 shrink-0" />
                <span>{t.footer.address}</span>
              </a>
            </li>
            <li className="text-white/55">{t.footer.hours}</li>
          </ul>

          <h4 className="mt-8 font-heading font-bold text-white text-sm uppercase tracking-[0.2em]">{t.footer.certifications}</h4>
          <ul className="mt-4 space-y-2">
            {t.footer.certs.map((c, i) => {
              const Icon = CERT_ICONS[i % CERT_ICONS.length];
              return (
                <li key={c} className="inline-flex items-center gap-2 mr-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/75">
                  <Icon size={14} className={i % 2 === 0 ? "text-cool" : "text-heat"} />
                  {c}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/50">© {year} Aarón Heating &amp; Cooling LLC. {t.footer.rights}</p>
          <p className="text-xs text-white/40">DC · MD · VA</p>
        </div>
      </div>
    </footer>
  );
};
